import {
  useEffect,
  useMemo,
  useRef,
  useState,
  Dispatch,
  ReactNode,
  RefObject,
  SetStateAction,
  ChangeEvent,
  useCallback,
} from "react"
import { useSearchParams } from "@/hooks/useSearchParams"
import { useTranslation } from "react-i18next"
import { ListSubheader, MenuItem } from "@mui/material"
import { createPortal } from "react-dom"
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid"
import classNames from "classnames"
import { isUndefined } from "lodash"

import Table from "@/components/table/Table"
import Pagination from "@/components/table/Pagination"
import Chip from "@/components/chip/Chip"
import BasicButton from "@/components/button/BasicButton"
import Checkbox, { BaseCheckbox } from "@/components/form/checkbox/Checkbox"
import Radio from "@/components/form/radio/Radio"
import Icon from "@/components/icon/Icon"
import Selector from "@/components/form/selector/Selector"
import { usePlayerGroupsQuery } from "@/hooks/api/useSettingQuery"
import { usePositionsQuery } from "@/hooks/api/usePositionQuery"
import { ActivityParticipatedPlayerType } from "@/types/activity"
import { CheckboxColor } from "@/types/components/checkbox"

import "./playerSelector.scss"
import { colorTokens } from "@/components/colorTokens"

type PlayerSelectorType = "group" | "position"
const PLAYER_SELECTOR_NAME_WIDTH = 248
const PLAYER_SELECTOR_POSITION_WIDTH = 86

// 두 GridRowSelectionModel의 선택 id 집합이 내용상 동일한지 비교
const isSameIdSelection = (a: GridRowSelectionModel, b: GridRowSelectionModel) => {
  if (a.type !== b.type) {
    return false
  }
  if (a.ids.size !== b.ids.size) {
    return false
  }
  for (const id of a.ids) {
    if (!b.ids.has(id)) {
      return false
    }
  }

  return true
}

export default function PlayerSelector({
  open,
  setOpen,
  selectedActivity,
  players,
  selectedPlayerList,
  setSelectedPlayerList,
  paramsVersion,
  singleSelection = false,
  liveUpdate = false,
  renderTrigger,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  selectedActivity: number[]
  players?: ActivityParticipatedPlayerType[]
  selectedPlayerList: ActivityParticipatedPlayerType[]
  setSelectedPlayerList: Dispatch<SetStateAction<ActivityParticipatedPlayerType[]>>
  paramsVersion: boolean
  singleSelection?: boolean
  liveUpdate?: boolean
  /** 트리거 커스텀 렌더 (기본: By Player 버튼). CSV Export 의 Select 형 트리거 등 */
  renderTrigger?: (args: {
    ref: RefObject<HTMLButtonElement | null>
    open: boolean
    onToggle: () => void
  }) => ReactNode
}) {
  const { t } = useTranslation()
  const searchParams = useSearchParams()
  const idfActivity = useMemo(() => searchParams.getAll("idfActivity").map(Number), [searchParams])

  const [tempSelectedPlayerList, setTempSelectedPlayerList] = useState<
    ActivityParticipatedPlayerType[]
  >([])
  const [playerLength, setPlayerLength] = useState<number>()
  const [selectedGroupList, setSelectedGroupList] = useState<number[]>([])
  const [groupListChecked, setGroupListChecked] = useState<boolean[]>([])
  const [selectedPositionList, setSelectedPositionList] = useState<string[]>([])
  const [positionChecked, setPositionChecked] = useState<boolean[]>([])
  const [playerRowSelectionModel, setPlayerRowSelectionModel] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set(),
  })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)
  // 그룹·포지션 필터를 조작했을 때만 선택 목록을 다시 맞추기 위한 플래그.
  // "keep" = 기존 체크 상태 유지(필터에서 빠진 선수만 해제), "all" = 필터 초기화 → 전체 선택.
  const pendingFilterSyncRef = useRef<"keep" | "all" | null>(null)
  // 직전 렌더에서 표에 보이던 선수 목록 — 필터로 새로 들어온 선수를 구분한다.
  const visiblePlayerListRef = useRef<ActivityParticipatedPlayerType[]>([])
  // 그룹·포지션 체크 상태를 마지막으로 초기화한 옵션 목록 (같은 옵션이면 다시 초기화하지 않는다)
  const groupInitSignatureRef = useRef<string | null>(null)
  const positionInitSignatureRef = useRef<string | null>(null)
  // liveUpdate 동기화가 마지막으로 반영한 선수 id 집합 시그니처 (중복 setState 방지)
  const liveSyncSignatureRef = useRef<string | null>(null)
  // maxHeight 는 초기값에만 있고 갱신 시에는 top/left/width 만 넣는다 → optional 로 선언
  const dropdownPositionRef = useRef<{
    top: number
    left: number
    width: number
    maxHeight?: string
  }>({ top: 0, left: 0, width: 0, maxHeight: "600px" })
  const [dropdownPosition, setDropdownPosition] = useState<any>({})
  const [groupSelectorOpen, setGroupSelectorOpen] = useState(false)
  const [positionSelectorOpen, setPositionSelectorOpen] = useState(false)

  useEffect(() => {
    if (!groupSelectorOpen && !positionSelectorOpen) {
      return
    }

    const preventScroll = (e: WheelEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        e.preventDefault()
      }
    }

    window.addEventListener("wheel", preventScroll, { passive: false })

    return () => {
      window.removeEventListener("wheel", preventScroll)
    }
  }, [groupSelectorOpen, positionSelectorOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement

      if (
        target.closest(".player-group-selector-menu") ||
        target.closest(".player-position-selector-menu")
      ) {
        return
      }

      if (
        open &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
        setSelectedPlayerList(tempSelectedPlayerList)
        setPlayerLength(tempSelectedPlayerList.length)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open, setOpen, setSelectedPlayerList, tempSelectedPlayerList])

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current || !open) {
      return
    }

    const rect = buttonRef.current.getBoundingClientRect()
    const minTableWidth = 440

    const newPosition = {
      top: rect.bottom,
      left: rect.right - minTableWidth,
      width: minTableWidth,
    }

    if (
      dropdownPositionRef.current.top !== newPosition.top ||
      dropdownPositionRef.current.left !== newPosition.left ||
      dropdownPositionRef.current.width !== newPosition.width
    ) {
      setDropdownPosition(newPosition)
      dropdownPositionRef.current = newPosition
    }
  }, [open])

  useEffect(() => {
    if (open) {
      updateDropdownPosition()
    }

    const handleScroll = () => {
      if (groupSelectorOpen || positionSelectorOpen) {
        return
      }

      if (open) {
        setOpen(false)
      }
    }

    const handleResize = () => {
      requestAnimationFrame(updateDropdownPosition)
    }

    if (open) {
      window.addEventListener("scroll", handleScroll, { passive: true })
      window.addEventListener("resize", handleResize, { passive: true })
    }

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleResize)
    }
  }, [open, updateDropdownPosition, setOpen, groupSelectorOpen, positionSelectorOpen])

  const tableColumns: GridColDef[] = [
    {
      field: "name",
      renderCell(params) {
        return (
          <p>
            {params.row.uniformNumber} {params.row.name}
          </p>
        )
      },
      renderHeader: () => <p>{t("player")}</p>,
      width: PLAYER_SELECTOR_NAME_WIDTH,
      sortable: false,
    },
    {
      field: "position",
      renderHeader: () => <p>{t("position")}</p>,
      width: PLAYER_SELECTOR_POSITION_WIDTH,
      sortable: false,
    },
  ]

  const { data: playerGroupList } = usePlayerGroupsQuery()
  // 드롭다운은 그룹 클러스터 순서(ordering=group)로 받아 그대로 신뢰(그룹 묶음/순서 백엔드 단일 출처).
  const { data: positionList } = usePositionsQuery("group")

  const playerGroupOptions = useMemo(() => {
    if (!playerGroupList || !idfActivity) {
      return []
    }

    return [
      ...playerGroupList.teamPlayerGroups
        .filter((item) => {
          return (players ?? [])
            .map((player) => player.idfPlayerGroup)
            .includes(item.idfTeamPlayerGroup)
        })
        .map((group) => ({
          value: group.idfTeamPlayerGroup,
          label: group.name,
        })),
    ]
  }, [playerGroupList, players])

  const positionOptions = useMemo(() => {
    if (!positionList || !idfActivity) {
      return []
    }

    return [
      ...positionList
        .filter((item) => {
          return (players ?? []).map((player) => player.position).includes(item.code)
        })
        .map((position) => ({
          value: position.code,
          label: position.code,
          group: position.group,
        })),
    ]
  }, [positionList, players])

  const positionGroupOptions = useMemo(() => {
    const groupedOptions = positionOptions.reduce(
      (acc, option) => {
        const group = option.group
        if (!acc[group]) {
          acc[group] = []
        }
        acc[group].push(option)

        return acc
      },
      {} as Record<string, { value: string; label: string; group: string }[]>,
    )

    return Object.entries(groupedOptions).map(([group, options]) => ({
      group,
      options,
    }))
  }, [positionOptions])

  const participatedPlayerList = useMemo(() => {
    if (!players) {
      return []
    }

    return players
      .filter((player: ActivityParticipatedPlayerType) => {
        let isPositionSelected = false
        let isGroupSelected = false

        // 그룹 옵션 자체가 없거나 체크 상태 초기화 전이면 그룹 필터 패스.
        // (selectedGroupList 가 비었는지로 판정하면 "모든 그룹 체크 해제" 가 초기화 전과
        //  구분되지 않아 필터가 통째로 무시된다 — 포지션 쪽과 동일하게 체크 배열로 판정한다)
        if (playerGroupOptions.length === 0 || groupListChecked.length === 0) {
          isGroupSelected = true
        } else if (groupListChecked.every((checked) => checked)) {
          isGroupSelected = true
        } else {
          isGroupSelected =
            !player.idfPlayerGroup || selectedGroupList.includes(player.idfPlayerGroup)
        }

        // 포지션 옵션 자체가 없으면 포지션 필터 패스
        if (positionOptions.length === 0) {
          isPositionSelected = true
        } else if (selectedPositionList.length === 0) {
          isPositionSelected = false
        } else if (positionChecked.every((checked) => checked)) {
          isPositionSelected = true
        } else {
          isPositionSelected = !player.position || selectedPositionList.includes(player.position)
        }

        return isGroupSelected && isPositionSelected
      })
      .sort((a, b) => {
        return a.uniformNumber - b.uniformNumber
      })
  }, [
    selectedPositionList,
    selectedGroupList,
    groupListChecked,
    positionChecked,
    players,
    playerGroupOptions,
    positionOptions,
  ])

  useEffect(() => {
    if (!liveUpdate) {
      return
    }

    // participatedPlayerList는 memo가 재계산될 때마다 내용이 같아도 새 배열 ref가 된다.
    // 내용(선수 id 집합)이 실제로 바뀌지 않았으면 setState를 건너뛴다 —
    // 그렇지 않으면 초기 수렴 과정에서 setSelectedPlayerList가 매 렌더 호출되어
    // "Maximum update depth" 경고가 연속 발생한다.
    const idsSignature = participatedPlayerList.map((player) => player.idfPlayer).join(",")
    if (liveSyncSignatureRef.current === idsSignature) {
      return
    }
    liveSyncSignatureRef.current = idsSignature

    setTempSelectedPlayerList(participatedPlayerList)
    setSelectedPlayerList(participatedPlayerList)
    setPlayerLength(participatedPlayerList.length)
    setPlayerRowSelectionModel({
      type: "include",
      ids: new Set(participatedPlayerList.map((player) => player.idfPlayer)),
    })
  }, [participatedPlayerList])

  // 그룹·포지션 필터를 바꾸면 표시 대상 선수가 바뀌지만, liveUpdate 가 아닌 화면(리포트 필터 등)은
  // 표의 체크 상태가 그대로 남아 필터가 결과에 반영되지 않는다.
  // 필터를 조작한 렌더에서만 선택 목록을 표시 대상에 다시 맞춘다.
  useEffect(() => {
    const syncMode = pendingFilterSyncRef.current
    const previousVisibleList = visiblePlayerListRef.current

    visiblePlayerListRef.current = participatedPlayerList

    if (liveUpdate || !syncMode) {
      return
    }

    pendingFilterSyncRef.current = null

    const previousVisibleIds = new Set(previousVisibleList.map((player) => player.idfPlayer))
    const selectedIds = new Set(tempSelectedPlayerList.map((player) => player.idfPlayer))
    const nextSelection =
      syncMode === "all" && !singleSelection
        ? participatedPlayerList
        : participatedPlayerList.filter((player) =>
            singleSelection
              ? selectedIds.has(player.idfPlayer)
              : // 필터로 새로 들어온 선수는 기본 선택(필터 해제 시 원복), 원래 보이던 선수는 체크 상태 유지
                !previousVisibleIds.has(player.idfPlayer) || selectedIds.has(player.idfPlayer),
          )

    setTempSelectedPlayerList(nextSelection)
    setPlayerRowSelectionModel({
      type: "include",
      ids: new Set(nextSelection.map((player) => player.idfPlayer)),
    })
  }, [participatedPlayerList])

  const onClickRow = (item: { row: ActivityParticipatedPlayerType }) => {
    if (singleSelection) {
      setTempSelectedPlayerList([item.row])
      setPlayerRowSelectionModel({
        type: "include",
        ids: new Set([item.row.idfPlayer]),
      })
    } else {
      if (tempSelectedPlayerList.includes(item.row)) {
        setTempSelectedPlayerList(tempSelectedPlayerList.filter((idf) => idf !== item.row))
      } else {
        setTempSelectedPlayerList([...tempSelectedPlayerList, item.row])
      }
    }
  }

  const renderSelectorValue = (selected: string[], type: PlayerSelectorType) => {
    if (selected.length === (type === "group" ? playerGroupOptions : positionOptions).length) {
      return <div>{t("all")}</div>
    }
    if (!selected.length) {
      return <span className={`player-${type}-selector-empty`}>{t("select")}</span>
    }

    return <div>{selected.length} selected</div>
  }

  const onClickAll = (type: PlayerSelectorType) => {
    const checkedArray = type === "group" ? groupListChecked : positionChecked
    const setCheckedArray = type === "group" ? setGroupListChecked : setPositionChecked
    const options = type === "group" ? playerGroupOptions : positionOptions
    const setSelectedList = (
      type === "group" ? setSelectedGroupList : setSelectedPositionList
    ) as Dispatch<SetStateAction<unknown[]>>

    pendingFilterSyncRef.current = "keep"

    if (checkedArray.every((checked) => checked)) {
      setCheckedArray(checkedArray.map(() => false))
      setSelectedList([])
    } else {
      setCheckedArray(checkedArray.map(() => true))
      setSelectedList(
        options.map((option) => (type === "group" ? Number(option.value) : String(option.value))),
      )
    }
  }

  const onClickSelectItem = (
    type: PlayerSelectorType,
    option: { value: unknown; label: string; group?: string },
    index: number,
  ) => {
    const tempChecked = [...(type === "group" ? groupListChecked : positionChecked)]
    const setChecked = type === "group" ? setGroupListChecked : setPositionChecked
    const selectedList =
      type === "group" ? selectedGroupList : (selectedPositionList as Array<unknown>)
    const setSelectedList = (
      type === "group" ? setSelectedGroupList : setSelectedPositionList
    ) as Dispatch<SetStateAction<unknown[]>>

    tempChecked[index] = !tempChecked[index]
    if (selectedList.includes(type === "group" ? Number(option.value) : option.value)) {
      setSelectedList(
        [...selectedList].filter(
          (idf) => idf !== (type === "group" ? Number(option.value) : option.value),
        ),
      )
    } else {
      setSelectedList([...selectedList, type === "group" ? Number(option.value) : option.value])
    }

    setChecked(tempChecked)
    pendingFilterSyncRef.current = "keep"
  }

  const onClickReset = useCallback(async () => {
    setGroupListChecked(playerGroupOptions.map(() => true))
    setSelectedGroupList(playerGroupOptions.map((option) => Number(option.value)))
    setPositionChecked(positionOptions.map(() => true))
    setSelectedPositionList(positionOptions.map((option) => option.value))
    pendingFilterSyncRef.current = "all"
  }, [playerGroupOptions, positionOptions])

  useEffect(() => {
    if (!paramsVersion) {
      return
    }

    const idfPlayerList = searchParams.get("idfPlayer")?.split(",").map(Number) || []
    const newList = participatedPlayerList.filter((player) =>
      idfPlayerList.includes(player.idfPlayer),
    )
    setSelectedPlayerList(newList)
    setTempSelectedPlayerList(newList)
  }, [searchParams])

  useEffect(() => {
    if (!open) {
      return
    }
    setTempSelectedPlayerList(selectedPlayerList)
    setPlayerLength(selectedPlayerList.length)
  }, [selectedPlayerList, open])

  // 옵션 목록이 실제로 바뀌었을 때만 "전체 선택" 으로 초기화한다.
  // idfActivity 는 searchParams 가 바뀔 때마다 새 배열이라, 이걸 의존성에 두면
  // View Report 로 URL 이 갱신될 때마다 사용자가 해제해 둔 체크가 전부 되돌아온다.
  useEffect(() => {
    const groupOptions = playerGroupOptions.filter((option) => !!Number(option.value))
    const optionSignature = groupOptions.map((option) => option.value).join(",")

    if (groupInitSignatureRef.current === optionSignature) {
      return
    }
    groupInitSignatureRef.current = optionSignature

    setSelectedGroupList(groupOptions.map((group) => Number(group.value)))
    setGroupListChecked(groupOptions.map(() => true))
  }, [playerGroupOptions])

  useEffect(() => {
    const optionSignature = positionOptions.map((option) => option.value).join(",")

    if (positionInitSignatureRef.current === optionSignature) {
      return
    }
    positionInitSignatureRef.current = optionSignature

    setSelectedPositionList(positionOptions.map((position) => position.value))
    setPositionChecked(positionOptions.map(() => true))
  }, [positionOptions])

  useEffect(() => {
    if (selectedPlayerList) {
      setTempSelectedPlayerList(selectedPlayerList)
      setPlayerLength(selectedPlayerList.length)
    }
  }, [selectedPlayerList])

  useEffect(() => {
    if (!open) {
      return
    }
    if (tempSelectedPlayerList.length) {
      setPlayerRowSelectionModel({
        type: "include",
        ids: new Set(tempSelectedPlayerList.map((player) => player.idfPlayer)),
      })
    }
  }, [tempSelectedPlayerList, open])

  const handleTriggerToggle = () => {
    if (open) {
      setSelectedPlayerList(tempSelectedPlayerList)
      setPlayerLength(tempSelectedPlayerList.length)
    }
    setOpen(!open)
  }

  return (
    <div key={"player-selector"} className="player-selector-container">
      {renderTrigger ? (
        renderTrigger({ ref: buttonRef, open, onToggle: handleTriggerToggle })
      ) : (
        <BasicButton
          className={open ? "open" : ""}
          size="md"
          type="default-outlined"
          ref={buttonRef}
          onClick={handleTriggerToggle}>
          <span>
            <p>{t("byPlayer")}</p>
            {players && !isUndefined(playerLength) && (
              <Chip type="number" label={playerLength.toString()}></Chip>
            )}
            <Icon
              type={open ? "arrow-up" : "arrow-down"}
              size={16}
              color={colorTokens.field["ico-base"]}
            />
          </span>
        </BasicButton>
      )}
      {createPortal(
        <div
          ref={dropdownRef}
          className={classNames(
            "report-player-filter-popover-wrapper",
            open ? "visible" : "hidden",
          )}
          style={dropdownPosition}>
          <div className="report-player-filter-popover">
            <div className="player-popover-selector-wrapper">
              <span className="player-popover-label-wrapper">
                <p>{t("playerGroup")}</p>
                <Selector
                  type="select"
                  className="player-group-selector"
                  displayEmpty
                  placeholder={t("select")}
                  multiple
                  open={groupSelectorOpen}
                  onOpen={() => setGroupSelectorOpen(true)}
                  onClose={() => setGroupSelectorOpen(false)}
                  onMouseDown={(e) => e.stopPropagation()}
                  MenuProps={{
                    PaperProps: {
                      className: "player-group-selector-menu",
                    },
                  }}
                  value={selectedGroupList.map(String) || []}
                  onChange={() => null}
                  renderValue={(selected) => renderSelectorValue(selected, "group")}>
                  <MenuItem
                    key={"all"}
                    className={classNames("player-group-item", "all")}
                    onClick={() => onClickAll("group")}>
                    <Checkbox
                      label={t("all")}
                      checked={groupListChecked.every((checked) => checked)}
                      indeterminate={
                        groupListChecked.some((checked) => checked) &&
                        !groupListChecked.every((checked) => checked)
                      }
                      disabled={
                        (!selectedActivity.length && !idfActivity.length) ||
                        !playerGroupOptions.length
                      }
                      color={CheckboxColor.PLATINUM}
                    />
                  </MenuItem>
                  {playerGroupOptions.map((option, index) => {
                    return (
                      <MenuItem
                        key={option.value}
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          onClickSelectItem("group", option, index)
                        }}>
                        <Checkbox
                          key={option.value}
                          label={option.label}
                          checked={groupListChecked[index] || false}
                          sx={{ pointerEvents: "none" }}
                          color={CheckboxColor.PLATINUM}
                        />
                      </MenuItem>
                    )
                  })}
                </Selector>
              </span>
              <span className="player-popover-label-wrapper">
                <p>{t("position")}</p>
                <Selector
                  type="select"
                  className="player-position-selector"
                  multiple
                  open={positionSelectorOpen}
                  onOpen={() => setPositionSelectorOpen(true)}
                  onClose={() => setPositionSelectorOpen(false)}
                  onMouseDown={(e) => e.stopPropagation()}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      className: "player-position-selector-menu",
                    },
                  }}
                  value={selectedPositionList || []}
                  onChange={() => null}
                  renderValue={(selected) => renderSelectorValue(selected, "position")}>
                  <MenuItem
                    className={classNames("player-position-group-item", "all")}
                    key={"all"}
                    value={"all"}
                    onClick={() => onClickAll("position")}>
                    <Checkbox
                      label={t("all")}
                      checked={positionChecked.every((checked) => checked)}
                      indeterminate={
                        positionChecked.some((checked) => checked) &&
                        !positionChecked.every((checked) => checked)
                      }
                      disabled={!idfActivity.length && !selectedActivity.length}
                      color={CheckboxColor.PLATINUM}
                    />
                  </MenuItem>
                  {positionGroupOptions.map((group, index) => {
                    return (
                      <div key={group.group}>
                        <ListSubheader className="player-position-group-sub" key={group.group}>
                          {group.group}
                        </ListSubheader>
                        {positionGroupOptions[index].options.map((option, optionIndex) => {
                          const previousCount = positionGroupOptions
                            .slice(0, index)
                            .reduce((acc, curr) => acc + curr.options.length, 0)
                          const itemIndex = previousCount + optionIndex

                          return (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                onClickSelectItem("position", option, itemIndex)
                              }}>
                              <Checkbox
                                key={option.value}
                                label={option.label}
                                checked={positionChecked[itemIndex] || false}
                                sx={{ pointerEvents: "none" }}
                                color={CheckboxColor.PLATINUM}
                              />
                            </MenuItem>
                          )
                        })}
                      </div>
                    )
                  })}
                </Selector>
              </span>
            </div>
            {/* 팝오버가 닫혀 있을 때는 DataGrid를 마운트하지 않는다.
                visibility:hidden 상태로 상시 마운트하면 MUI DataGrid의 dimension effect가
                측정 루프에 빠져 Maximum update depth 로 크래시한다(특히 liveUpdate로 rows가 갱신될 때).
                selectedPlayerList 라이브 동기화는 컴포넌트 본문 effect에서 처리되므로 영향 없음. */}
            {open && (
              <Table
                columns={tableColumns}
                rows={participatedPlayerList}
                getRowId={(row) => row.idfPlayer}
                rowSelectionModel={playerRowSelectionModel}
                onRowClick={onClickRow}
                slots={{
                  pagination: Pagination,
                }}
                selectionType={singleSelection ? "radio" : "checkbox"}
                columnHeaderHeight={41}
                selectionElement={(props) => {
                  const isHeader =
                    (props as any).slotProps?.htmlInput?.["aria-label"]?.includes("all") ||
                    (props as any)["aria-label"]?.includes("all")

                  if (singleSelection) {
                    if (isHeader) {
                      return null
                    }

                    const onChangeRadio = (e: any, checked: boolean) => {
                      if (checked) {
                        isFirstRender.current = false

                        return props.onChange?.(e, checked)
                      }
                    }

                    return (
                      <Radio checked={props.checked} onChange={onChangeRadio} value={props.value} />
                    )
                  } else {
                    const onChangeTableCheckbox = (
                      e: ChangeEvent<HTMLInputElement>,
                      checked: boolean,
                    ) => {
                      if (isHeader) {
                        if (props.checked && !props.indeterminate) {
                          setTempSelectedPlayerList([])
                          setPlayerRowSelectionModel({
                            type: "include",
                            ids: new Set(),
                          })
                          if (liveUpdate) {
                            setSelectedPlayerList([])
                            setPlayerLength(0)
                          }

                          return
                        }

                        setTempSelectedPlayerList(participatedPlayerList)
                        setPlayerRowSelectionModel({
                          type: "include",
                          ids: new Set(participatedPlayerList.map((player) => player.idfPlayer)),
                        })
                        isFirstRender.current = false
                        if (liveUpdate) {
                          setSelectedPlayerList(participatedPlayerList)
                          setPlayerLength(participatedPlayerList.length)
                        }

                        return
                      } else {
                        isFirstRender.current = false

                        return props.onChange?.(e, checked)
                      }
                    }

                    return (
                      <BaseCheckbox
                        checked={props.checked}
                        indeterminate={props.indeterminate}
                        onChange={onChangeTableCheckbox}
                        disabled={props.disabled}
                      />
                    )
                  }
                }}
                onRowSelectionModelChange={(model) => {
                  if (isFirstRender.current) {
                    return
                  }

                  // 프로그래매틱 setPlayerRowSelectionModel(새 Set) → DataGrid가 onChange 재호출 →
                  // setSelectedPlayerList/setPlayerRowSelectionModel 재설정으로 무한 루프(Maximum update depth).
                  // 선택 내용이 실제로 바뀌지 않았으면 여기서 끊는다.
                  if (isSameIdSelection(model, playerRowSelectionModel)) {
                    return
                  }

                  isFirstRender.current = false
                  setPlayerRowSelectionModel(model)
                  const newSelection = participatedPlayerList.filter((player) =>
                    model.ids.has(player.idfPlayer),
                  )
                  setTempSelectedPlayerList(newSelection)
                  if (liveUpdate) {
                    setSelectedPlayerList(newSelection)
                    setPlayerLength(newSelection.length)
                  }
                }}
                disableColumnResize
                disableColumnMenu
                disableColumnSorting
                disableVirtualization
              />
            )}

            <div className="selector-reset-wrapper">
              <button className="selector-reset-button" onClick={onClickReset}>
                <p>{t("button.reset")}</p>
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
